import React from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    LinearProgress,
    Stack,
    Chip,
    Paper
} from '@mui/material';
import {
    TrendingUp,
    Timeline,
    EmojiEvents,
    LocalFireDepartment,
    Star
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';

const ProgressTracker = ({ userStats, userBadges = [], rewardHistory = [] }) => {
    if (!userStats) {
        return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h5">Loading your eco progress...</Typography>
            </Box>
        );
    }

    const { currentLevel, nextLevel, progressPercentage, totalPoints } = userStats;

    // Calculate various progress metrics
    const streakProgress = Math.min(100, (userStats.currentStreak / 30) * 100);
    const classificationsToNextBadge = Math.max(0, 100 - userStats.totalClassifications);
    const badgeProgress = (userBadges.length / 5) * 100; // 5 total badges available

    const progressMetrics = [
        {
            title: 'Level Progress',
            current: totalPoints,
            target: nextLevel?.minPoints || totalPoints,
            progress: progressPercentage,
            color: currentLevel?.color || '#43a047',
            icon: <Star />,
            description: `${Math.round(progressPercentage)}% to ${nextLevel?.name || 'Max Level'}`
        },
        {
            title: 'Monthly Streak Goal',
            current: userStats.currentStreak,
            target: 30,
            progress: streakProgress,
            color: '#ff5722',
            icon: <LocalFireDepartment />,
            description: `${userStats.currentStreak}/30 days - ${30 - userStats.currentStreak} days to go`
        },
        {
            title: 'Badge Collection',
            current: userBadges.length,
            target: 5,
            progress: badgeProgress,
            color: '#ffc400',
            icon: <EmojiEvents />,
            description: `${userBadges.length}/5 badges unlocked`
        },
        {
            title: 'Marathoner Progress',
            current: userStats.totalClassifications,
            target: 100,
            progress: Math.min(100, (userStats.totalClassifications / 100) * 100),
            color: '#2196f3',
            icon: <Timeline />,
            description: `${Math.max(0, 100 - userStats.totalClassifications)} classifications to Marathoner badge`
        }
    ];

    // Recent achievements from reward history
    const recentAchievements = rewardHistory.slice(0, 5);

    return (
        <Box>
            <Typography variant="h4" fontWeight={800} sx={{ mb: 1, color: '#1b5e20', textAlign: 'center' }}>
                📈 Your Eco Journey Progress
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 4, textAlign: 'center' }}>
                Track your environmental impact and upcoming milestones
            </Typography>

            {/* Progress Metrics Grid */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                {progressMetrics.map((metric, index) => (
                    <Grid item xs={12} md={6} key={index}>
                        <Card elevation={6} sx={{
                            borderRadius: 4,
                            background: `linear-gradient(135deg, ${alpha(metric.color, 0.1)}, rgba(255,255,255,0.95))`,
                            border: `2px solid ${alpha(metric.color, 0.2)}`,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                transform: 'translateY(-5px)',
                                boxShadow: `0 12px 28px ${alpha(metric.color, 0.25)}`
                            }
                        }}>
                            <CardContent sx={{ p: 3 }}>
                                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                                    <Box sx={{
                                        width: 50,
                                        height: 50,
                                        borderRadius: '50%',
                                        background: `linear-gradient(135deg, ${metric.color}, ${alpha(metric.color, 0.8)})`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'white'
                                    }}>
                                        {metric.icon}
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="h6" fontWeight={700} sx={{ color: '#1b5e20' }}>
                                            {metric.title}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {metric.description}
                                        </Typography>
                                    </Box>
                                    <Typography variant="h5" fontWeight={800} sx={{ color: metric.color }}>
                                        {Math.round(metric.progress)}%
                                    </Typography>
                                </Stack>

                                <LinearProgress
                                    variant="determinate"
                                    value={metric.progress}
                                    sx={{
                                        height: 12,
                                        borderRadius: 6,
                                        backgroundColor: alpha(metric.color, 0.2),
                                        '& .MuiLinearProgress-bar': {
                                            background: `linear-gradient(90deg, ${metric.color}, ${alpha(metric.color, 0.8)})`,
                                            borderRadius: 6,
                                            boxShadow: `0 2px 8px ${alpha(metric.color, 0.4)}`
                                        }
                                    }}
                                />

                                <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Current: {metric.current}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Target: {metric.target}
                                    </Typography>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Current Level Summary */}
            <Paper elevation={4} sx={{
                p: 4,
                borderRadius: 4,
                mb: 4,
                background: `linear-gradient(135deg, ${alpha(currentLevel?.color || '#43a047', 0.15)}, rgba(255,255,255,0.95))`,
                border: `3px solid ${alpha(currentLevel?.color || '#43a047', 0.3)}`
            }}>
                <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} md={8}>
                        <Typography variant="h5" fontWeight={800} sx={{ mb: 2, color: '#1b5e20' }}>
                            🎯 Current Achievement Status
                        </Typography>
                        <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb: 2 }}>
                            <Chip
                                icon={<span style={{ fontSize: '1.2rem' }}>{currentLevel?.icon}</span>}
                                label={`${currentLevel?.name || 'Rookie'} Level`}
                                sx={{
                                    background: `linear-gradient(135deg, ${currentLevel?.color || '#43a047'}, ${alpha(currentLevel?.color || '#43a047', 0.8)})`,
                                    color: 'white',
                                    fontWeight: 700,
                                    fontSize: '1rem'
                                }}
                            />
                            <Chip
                                icon={<EmojiEvents />}
                                label={`${userBadges.length} Badges`}
                                sx={{
                                    background: 'linear-gradient(135deg, #ffc400, #ff8f00)',
                                    color: 'white',
                                    fontWeight: 700
                                }}
                            />
                            <Chip
                                icon={<LocalFireDepartment />}
                                label={`${userStats.currentStreak} Day Streak`}
                                sx={{
                                    background: 'linear-gradient(135deg, #ff5722, #ff8a65)',
                                    color: 'white',
                                    fontWeight: 700
                                }}
                            />
                            <Chip
                                icon={<TrendingUp />}
                                label={`${userStats.totalPoints} Total Points`}
                                sx={{
                                    background: 'linear-gradient(135deg, #2196f3, #64b5f6)',
                                    color: 'white',
                                    fontWeight: 700
                                }}
                            />
                        </Stack>
                        <Typography variant="body1" color="text.secondary">
                            You're making excellent progress in your environmental journey!
                            {nextLevel && ` Keep going to reach ${nextLevel.name} level with ${nextLevel.minPoints - totalPoints} more points.`}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h2" sx={{ mb: 1 }}>
                                {currentLevel?.icon || '🌱'}
                            </Typography>
                            <Typography variant="h4" fontWeight={800} sx={{ color: currentLevel?.color || '#43a047' }}>
                                {currentLevel?.name || 'Rookie'}
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {/* Recent Achievements */}
            {recentAchievements.length > 0 && (
                <Paper elevation={4} sx={{ p: 3, borderRadius: 4, background: 'linear-gradient(135deg, #f1f8e9, #e8f5e8)' }}>
                    <Typography variant="h6" fontWeight={700} sx={{ mb: 3, color: '#2e7d32', display: 'flex', alignItems: 'center', gap: 1 }}>
                        🎉 Recent Eco Achievements
                    </Typography>
                    <Stack spacing={2}>
                        {recentAchievements.map((achievement, index) => (
                            <Box
                                key={index}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    p: 2,
                                    borderRadius: 3,
                                    background: 'rgba(255,255,255,0.8)',
                                    border: '1px solid rgba(67, 160, 71, 0.2)',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        background: 'rgba(255,255,255,0.95)',
                                        transform: 'translateX(8px)'
                                    }
                                }}
                            >
                                <Box sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: '50%',
                                    background: 'linear-gradient(135deg, #43a047, #66bb6a)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    mr: 3,
                                    fontSize: '1.2rem'
                                }}>
                                    {achievement.eventType === 'CLASSIFICATION' ? '♻️' :
                                     achievement.eventType === 'BADGE_UNLOCK' ? '🏆' :
                                     achievement.eventType === 'LEVEL_UP' ? '⬆️' : '⭐'}
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="body1" fontWeight={600}>
                                        {achievement.description || 'Eco achievement unlocked!'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {new Date(achievement.awardedAt).toLocaleDateString('en-US', {
                                            month: 'short',
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                        })}
                                    </Typography>
                                </Box>
                                <Typography variant="h6" fontWeight={700} sx={{ color: '#43a047' }}>
                                    +{achievement.points}
                                </Typography>
                            </Box>
                        ))}
                    </Stack>
                </Paper>
            )}

            {/* Next Goals */}
            <Paper elevation={4} sx={{ mt: 4, p: 3, borderRadius: 4, background: 'linear-gradient(135deg, #fff3e0, #ffe0b2)' }}>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 3, color: '#e65100', display: 'flex', alignItems: 'center', gap: 1 }}>
                    🎯 Your Next Eco Goals
                </Typography>
                <Grid container spacing={2}>
                    {nextLevel && (
                        <Grid item xs={12} md={6}>
                            <Box sx={{ p: 2, borderRadius: 2, background: 'rgba(255,255,255,0.8)' }}>
                                <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#e65100', mb: 1 }}>
                                    Level Up Goal
                                </Typography>
                                <Typography variant="body2">
                                    Reach {nextLevel.name} level by earning {nextLevel.minPoints - totalPoints} more points
                                </Typography>
                            </Box>
                        </Grid>
                    )}
                    {userStats.currentStreak < 7 && (
                        <Grid item xs={12} md={6}>
                            <Box sx={{ p: 2, borderRadius: 2, background: 'rgba(255,255,255,0.8)' }}>
                                <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#e65100', mb: 1 }}>
                                    Weekly Warrior Badge
                                </Typography>
                                <Typography variant="body2">
                                    Maintain a {7 - userStats.currentStreak} more day streak to unlock this epic badge
                                </Typography>
                            </Box>
                        </Grid>
                    )}
                    {userStats.totalClassifications < 100 && (
                        <Grid item xs={12} md={6}>
                            <Box sx={{ p: 2, borderRadius: 2, background: 'rgba(255,255,255,0.8)' }}>
                                <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#e65100', mb: 1 }}>
                                    Marathoner Badge
                                </Typography>
                                <Typography variant="body2">
                                    Complete {100 - userStats.totalClassifications} more classifications to earn this rare badge
                                </Typography>
                            </Box>
                        </Grid>
                    )}
                </Grid>
            </Paper>
        </Box>
    );
};

export default ProgressTracker;
